import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, MoreHorizontal, Filter, FileText, Users, Calendar, CheckCircle, XCircle, RefreshCw, Eye, Mail } from 'lucide-react';
import { getSurveys, createSurvey, updateSurvey, deleteSurvey } from '../utils/surveyApi';
import { mockSurveys } from '../utils/mockSurveyData';
import { getAuthHeaders } from '../utils/api';
import { API_BASE_URL, PDF_DISPLAY_GENERATE, PUBLIC_SEND_MAIL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const getPdfViewUrl = (sessionId) => {
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}/api/public/pdf-display/download/${sessionId}.pdf`;
};

const Survey = () => {
  const { user, userSites } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'active'
  });
  const [downloadingSession, setDownloadingSession] = useState(null);

  const buildSiteUrl = () => {
    const siteData = Array.isArray(userSites) ? userSites[0] : userSites;
    const siteSlug = siteData?.site_slug || user?.site_slug || '';
    const siteDomain = siteData?.domain || window.location.origin;
    if (!siteDomain) return null;
    const normalizedDomain = siteDomain.replace(/\/$/, '');
    return siteSlug ? `${normalizedDomain}/${siteSlug}` : normalizedDomain;
  };

  // Ensure surveys is always an array
  const safeSurveys = Array.isArray(surveys) ? surveys : [];

  // Get site_id from user login data
  const currentSiteId = userSites?.site_id || user?.site_id || null;

  // Fetch surveys on component mount and when site_id changes
  useEffect(() => {
    fetchSurveys();
  }, [currentSiteId]);

  // Ensure surveys is always an array
  useEffect(() => {
    if (!Array.isArray(surveys)) {
      console.warn('Surveys is not an array, resetting to empty array');
      setSurveys([]);
    }
  }, [surveys]);

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      // Prepare params with site_id if available
      const params = {};
      if (currentSiteId) {
        params.site_id = currentSiteId;
        console.log('🔍 Fetching surveys with site_id:', currentSiteId);
      } else {
        console.warn('⚠️ No site_id available, fetching all surveys');
      }
      
      // Try to fetch from API first
      const response = await getSurveys(params);
      console.log('API Response:', response); // Debug log
      
      // Always store the API response for display
      setApiResponse(response);
      
      if (response.success) {
        // Check if response.data exists and handle different data structures
        let surveyData = response.data;
        
        // Handle different possible API response structures
        if (response.data && response.data.data) {
          // If API returns { data: { data: [...] } }
          surveyData = response.data.data;
        } else if (response.data && response.data.surveys) {
          // If API returns { data: { surveys: [...] } }
          surveyData = response.data.surveys;
        } else if (response.data && response.data.items) {
          // If API returns { data: { items: [...] } }
          surveyData = response.data.items;
        } else if (response.data && response.data.results) {
          // If API returns { data: { results: [...] } }
          surveyData = response.data.results;
        }
        
        console.log('Processed survey data:', surveyData);
        
        if (Array.isArray(surveyData)) {
          console.log('Setting surveys:', surveyData);
          setSurveys(surveyData);
        } else {
          console.warn('API returned data but it\'s not an array:', surveyData);
          setSurveys([]);
        }
      } else {
        // API call failed
        console.warn('API call failed:', response.error);
        setSurveys([]);
      }
    } catch (error) {
      // API call threw an error
      console.warn('API error:', error);
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = { ...formData };
      if (!editingSurvey) {
        const siteUrl = buildSiteUrl();
        if (siteUrl) {
          payload.site_url = siteUrl;
        }
      }

      let response;
      if (editingSurvey) {
        response = await updateSurvey(editingSurvey.id, payload);
      } else {
        response = await createSurvey(payload);
      }

      if (response.success) {
        setShowModal(false);
        setEditingSurvey(null);
        resetForm();
        fetchSurveys();
      } else {
        // Fallback to mock data operations if API fails
        console.warn('API failed, using mock data operations:', response.error);
        if (editingSurvey) {
          // Update mock data
          setSurveys(prev => prev.map(survey => 
            survey.id === editingSurvey.id 
              ? { ...survey, ...payload, updated_at: new Date().toISOString() }
              : survey
          ));
        } else {
          // Create new mock survey
          const newSurvey = {
            id: safeSurveys.length > 0 ? Math.max(...safeSurveys.map(s => s.id)) + 1 : 1,
            ...payload,
            responses_count: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setSurveys(prev => [...(Array.isArray(prev) ? prev : []), newSurvey]);
        }
        setShowModal(false);
        setEditingSurvey(null);
        resetForm();
      }
    } catch (error) {
      // Fallback to mock data operations if API throws error
      console.warn('API error, using mock data operations:', error);
      if (editingSurvey) {
        setSurveys(prev => prev.map(survey => 
          survey.id === editingSurvey.id 
            ? { ...survey, ...formData, updated_at: new Date().toISOString() }
            : survey
        ));
      } else {
        const newSurvey = {
          id: safeSurveys.length > 0 ? Math.max(...safeSurveys.map(s => s.id)) + 1 : 1,
          ...formData,
          responses_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setSurveys(prev => [...(Array.isArray(prev) ? prev : []), newSurvey]);
      }
      setShowModal(false);
      setEditingSurvey(null);
      resetForm();
    }
  };

  const handleEdit = (survey) => {
    setEditingSurvey(survey);
    setFormData({
      title: survey.title,
      description: survey.description,
      start_date: survey.start_date,
      end_date: survey.end_date,
      status: survey.status
    });
    setShowModal(true);
  };

  const handleDownloadReport = async (survey, session) => {
    const sessionId = session || survey.session || survey.id || 'survey';
    setDownloadingSession(sessionId);
    console.log('📥 Downloading report for survey:', survey);
    try {
      const url = `${API_BASE_URL}${PDF_DISPLAY_GENERATE}`;
      const headers = getAuthHeaders();
      
      console.log('🔗 Making API call to:', url);
      console.log('📋 Request data:', {
        type: 'report',
        site_id: survey.site_id || 52,
        session: session || survey.session
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          type: 'report',
          site_id: survey.site_id || 52,
          session: session || survey.session
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Failed to generate report');
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || errorData.message || 'Failed to generate report');
      }

      // Construct file path from session/survey data
      const filePath = `${sessionId}.pdf`;
      
      // Construct public storage URL from API_BASE_URL
      // Pattern: API_BASE_URL/storage/public/{filePath}
      const baseUrl = API_BASE_URL.replace('/api', ''); // Remove /api to get base URL
      const publicStorageUrl = `${API_BASE_URL}/public/pdf-display/download/${filePath}`;
      
      
      // Open/download PDF from public storage
      const link = document.createElement('a');
      link.href = publicStorageUrl;
      link.download = filePath;
      link.target = '_blank'; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ PDF report downloaded successfully from public storage');
    } catch (error) {
      console.error('❌ Error generating report:', error);
      alert(`Unable to generate report: ${error.message || 'Please try again later.'}`);
    } finally {
      setDownloadingSession(null);
    }
  };

  const handleViewReport = (survey) => {
    const sessionId = survey.session || survey.id || 'survey';
    const baseUrl = API_BASE_URL.replace('', '');
    const viewUrl = `${baseUrl}/public/pdf-display/download/${sessionId}.pdf`;
    
    console.log('🔗 Viewing report URL:', viewUrl);
    window.open(viewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSendEmail = async (surveyId) => {
    try {
      const survey = surveys.find(s => s.id === surveyId);
      if (!survey) {
        alert('Survey not found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}${PUBLIC_SEND_MAIL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          session: survey.session || survey.id,
          site_id: survey.site_id || user?.site_id || 52,
          type: 'survey-continue'
        })
      });

      const data = await response.json();
      
      if (response.ok && (data.success || data.message)) {
        alert('Email sent successfully!');
      } else {
        throw new Error(data.error || data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert(`Failed to send email: ${error.message || 'Please try again later.'}`);
    }
  };

  const handleDelete = async (surveyId) => {
    if (window.confirm('Are you sure you want to delete this survey?')) {
      try {
        const response = await deleteSurvey(surveyId);
        if (response.success) {
          fetchSurveys();
        } else {
          // Fallback to mock data operations if API fails
          console.warn('API failed, using mock data operations:', response.error);
          setSurveys(prev => prev.filter(survey => survey.id !== surveyId));
        }
      } catch (error) {
        // Fallback to mock data operations if API throws error
        console.warn('API error, using mock data operations:', error);
        setSurveys(prev => prev.filter(survey => survey.id !== surveyId));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      status: 'active'
    });
  };

  const openCreateModal = () => {
    setEditingSurvey(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSurvey(null);
    resetForm();
  };

  const handleRowClick = (survey) => {
    setSelectedSurvey(survey);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedSurvey(null);
  };


  const filteredSurveys = safeSurveys
    .filter(survey =>
      (survey.fname && survey.fname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (survey.lname && survey.lname.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => (b.id || 0) - (a.id || 0)); // Sort by ID descending

  

  const getStatusBadge = (status) => {
    const statusConfig = {
      processed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      ongoing: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      draft: { color: 'bg-yellow-100 text-yellow-800', icon: FileText },
      inactive: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    // Ensure status is a string and has a valid value
    const safeStatus = status && typeof status === 'string' ? status : 'ongoing';
    const config = statusConfig[safeStatus] || statusConfig.ongoing;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Surveys</h1>
          <p className="text-gray-600">Manage and create surveys for your users.</p>
        </div>
        {/**<button 
          onClick={openCreateModal}
          className="btn-primary flex items-center space-x-2 mt-4 sm:mt-0"
        >
          <Plus className="h-5 w-5" />
          <span>Create Survey</span>
        </button>*/}
      </div>

      

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search surveys..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          {/**<div className="flex items-center space-x-3">
            <button className="btn-secondary flex items-center space-x-2">
              <Filter className="h-4 w-4" />
              <span>Filter</span>
            </button>
            
          </div>*/}
        </div>
      </div>

             {/* Surveys Table */}
       <div className="card">
         {/* Table Header Info */}
         <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
           <div className="flex items-center justify-between">
             <div>
               <h3 className="text-lg font-medium text-gray-900">Survey Responses</h3>
               <p className="text-sm text-gray-600">
                 {apiResponse && apiResponse.success 
                   ? `Showing ${safeSurveys.length} surveys` 
                   : `Showing ${safeSurveys.length} surveys (${safeSurveys.length > 0 ? 'from fallback data' : 'no data available'})`
                 }
               </p>
             </div>
             <div className="text-right">
               <p className="text-xs text-gray-500">
                 Last updated: {new Date().toLocaleTimeString()}
               </p>
             </div>
           </div>
         </div>
         
         <div className="overflow-x-auto table-container">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AccessCode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Off Track
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  On Track
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Created
                </th>
              </tr>
            </thead>
                         <tbody className="bg-white divide-y divide-gray-200">
               {safeSurveys.length === 0 ? (
                 <tr>
                   <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                     <div className="flex flex-col items-center space-y-2">
                       <FileText className="h-12 w-12 text-gray-300" />
                       <p className="text-lg font-medium">No surveys found</p>
                       {/**<p className="text-sm">
                         {apiResponse && apiResponse.success 
                           ? 'API returned no data or empty array' 
                           : 'No API response or using fallback data'
                         }
                       </p>*/}
                       <button
                         onClick={fetchSurveys}
                         className="btn-secondary mt-2"
                         style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                       >
                         Try Again
                       </button>
                     </div>
                   </td>
                 </tr>
               ) : filteredSurveys.length === 0 ? (
                 <tr>
                   <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                     <div className="flex flex-col items-center space-y-2">
                       <Search className="h-12 w-12 text-gray-300" />
                       <p className="text-lg font-medium">No surveys match your search</p>
                       <p className="text-sm">Try adjusting your search terms</p>
                     </div>
                   </td>
                 </tr>
               ) : (
                 filteredSurveys.map((survey) => (
                   <tr key={survey.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(survey)}>
                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                       <div className="flex items-center space-x-2">
                        {survey.status === 'processed' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (downloadingSession) return;
                              handleDownloadReport(survey, survey.session);
                            }}
                            className={`text-primary-600 hover:text-primary-900 flex items-center justify-center ${downloadingSession === (survey.session || survey.id || 'survey') ? 'opacity-70 cursor-not-allowed' : ''}`}
                            style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px', minWidth: '30px' }}
                            disabled={downloadingSession === (survey.session || survey.id || 'survey')}
                          >
                            {downloadingSession === (survey.session || survey.id || 'survey') ? (
                              <span className="flex items-center space-x-1">
                                <span className="inline-block h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                <span className="text-xs">Loading</span>
                              </span>
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {survey.status === 'processed' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewReport(survey);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}

          {survey.status === 'ongoing' && (
                           <button
                             onClick={(e) => {
                               e.stopPropagation();
                               if (window.confirm('Are you sure you want to send an email to continue this survey?')) {
                                 handleSendEmail(survey.id);
                               }
                             }}
                             className="text-red-600 hover:text-red-900"
                             style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                           >
                             <Mail className="h-4 w-4" />
                           </button>
                         )}

                         <button
                           onClick={(e) => {
                             e.stopPropagation();
                             handleDelete(survey.id);
                           }}
                           className="text-red-600 hover:text-red-900"
                           style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                         >
                           <Trash2 className="h-4 w-4" />
                         </button>

                         
                         
                         {/**<button className="text-gray-400 hover:text-gray-600">
                           <MoreHorizontal className="h-4 w-4" />
                         </button>*/}
                       </div>
                     </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{survey.fname || 'No First Name'}</div>
                          <div className="text-sm text-gray-500">{survey.lname || 'No Last Name'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-500">{survey.email || 'No Email'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-500">{survey.access_code || 'No Access Code'}</div>
                        </div>
                      </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(survey.status || 'ongoing')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="text-red-600 font-medium">
                            {survey.off_track || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="text-green-600 font-medium">
                            {survey.on_track || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="text-blue-600 font-medium">
                            {survey.total_score || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>
                            {survey.created_at ? new Date(survey.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </td>
                   </tr>
                 ))
               )}
             </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={closeModal}
        >
          <div 
            className="relative top-[10px] mx-auto p-5 border w-[400px] shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingSurvey ? 'Edit Survey' : 'Create Survey'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    required
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                    style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                  >
                    {editingSurvey ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Survey Details Modal */}
      {showDetailsModal && selectedSurvey && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={closeDetailsModal}
        >
          <div 
            className="relative top-[10px] mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Survey Details
                </h3>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Basic Information and Score Information - Side by Side */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Basic Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Basic Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Name:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedSurvey.fname || 'N/A'} {selectedSurvey.lname || ''}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Email:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedSurvey.email || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Access Code:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedSurvey.access_code || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status:</span>
                        <div>
                          {getStatusBadge(selectedSurvey.status || 'ongoing')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Score Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Score Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Off Track:</span>
                        <span className="text-sm font-medium text-red-600">
                          {selectedSurvey.off_track || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">On Track:</span>
                        <span className="text-sm font-medium text-green-600">
                          {selectedSurvey.on_track || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Total Score:</span>
                        <span className="text-sm font-medium text-blue-600">
                          {selectedSurvey.total_score || '0'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Additional Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Position:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.PositionPercent || '0 %'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Capability:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.CapabilityPercent || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Channel:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.ChannelPercent || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Buyer:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.BuyerPercent || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Communication:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.CommunicationPercent || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Conversation:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.ConversationsPercent || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Products:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.ProductPercent || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Management:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.ManagementPercent || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Services:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.ServicePercent || '0'}%
                      </span>
                    </div>
                  </div>
                </div>

                

                {/* Date Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Date Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Created:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedSurvey.created_at ? new Date(selectedSurvey.created_at).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={closeDetailsModal}
                  className="btn-primary"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Survey;
